/**
 * Sélecteur d'objectif d'un challenge — composant PARTAGÉ par l'ancien écran (onglet Challenges
 * d'`AccountPage`) et le nouveau (fiche et création dans Pilot). Les trois familles d'objectifs
 * viennent de `challengeObjectiveOptions` (`src/lib/challenge-objectives.ts`) : objectifs du
 * simulateur, critères de score, objectifs communs. Décision 4.1 (PR #7).
 */
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "./ui/select";
import { challengeObjectiveOptions, type ObjectiveFamily } from "../lib/challenge-objectives";

const NONE = "_none";
const FAMILIES: ObjectiveFamily[] = ["simulator", "criterion", "common"];

export function ChallengeObjectiveSelect({ value, onChange, simulatorName, criteria, exclude, allowNone, disabled, placeholder, ariaLabel }: {
  value: string;
  onChange: (value: string) => void;
  /** Nom du simulateur (famille 1 : ses objectifs historiques). */
  simulatorName?: string | null;
  /** Critères du simulateur (famille 2), tels que l'API les renvoie. */
  criteria: Array<{ key: string; type?: string | null; label?: string | null; labelFr?: string | null; labelEn?: string | null; labelSpoken?: string | null }>;
  /** Clé à ne pas proposer (l'objectif principal, dans le sélecteur de l'objectif secondaire). */
  exclude?: string | null;
  /** Objectif facultatif : propose « Aucun ». */
  allowNone?: boolean;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const { t, i18n } = useTranslation();
  const options = challengeObjectiveOptions(simulatorName, criteria, i18n.language).filter((o) => o.value !== exclude);
  return (
    <Select value={allowNone ? (value || NONE) : value} onValueChange={(v) => onChange(v === NONE ? "" : v)} disabled={disabled}>
      <SelectTrigger aria-label={ariaLabel}><SelectValue placeholder={placeholder ?? t("challenge.selectObjective")} /></SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value={NONE}>{t("common.none")}</SelectItem>}
        {FAMILIES.map((f) => {
          const inFamily = options.filter((o) => o.family === f);
          if (inFamily.length === 0) return null;
          return (
            <SelectGroup key={f}>
              <SelectLabel>{t(`challenge.objectiveFamily.${f}`)}</SelectLabel>
              {inFamily.map((o) => <SelectItem key={o.value} value={o.value}>{o.label ?? t(o.labelKey!)}</SelectItem>)}
            </SelectGroup>
          );
        })}
      </SelectContent>
    </Select>
  );
}
